/**
 * The reads `/studio/tasks` is drawn from, and the decisions the board and the list
 * table have to agree about.
 *
 * Kept out of the components for one reason: the board and the list are "the same
 * tasks in one table, sorted by due date" (`mockups/studio/tasks.html`'s own note),
 * so a filter that meant one thing in the columns and another in the rows would make
 * the toggle a lie.
 *
 * **The server filters, searches, sorts, pages and counts** (session 42). The URL's
 * facets become one input, `facetsToTaskFilters`, and the same input goes to
 * `tasks_list`, a page of fifty at a time, and to `tasks_summary`, which counts what
 * the list pages. No row is filtered, ordered or counted here: the page used to read
 * the hundred newest tasks and do all three itself, and a list sorted by due date
 * then left out every older task, the most overdue first.
 */

import { ClientSlugSchema, TASK_QUEUES, type TaskQueue, type TaskView } from '@fareast/shared'
import { hashKey, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { z } from 'zod'

import type { ApiError } from '../../../api/hooks.js'
import { studioPagesKey } from '../../../api/keys.js'
import { isApiProblem } from '../../../api/problem.js'
import {
  useStudioPages,
  useStudioRead,
  type StudioCallInput,
  type StudioOutput,
} from '../../../api/studio.js'
import { priorityWord, QUEUE_WORD } from '../../../components/studio/TaskCardFull.js'
import { preparedActionOf } from '../../../components/studio/taskWords.js'

/** The two segs in the topbar, as the URL carries them. */
export type BoardView = 'board' | 'list'

/** `tasks_summary`'s answer, as the RPC client types it. `TasksBoardSchema`'s shape. */
export type TasksSummary = StudioOutput<'tasks_summary'>

/**
 * Past this many columns the board is not a board.
 *
 * `.docs/20` §Edge cases: "Board with more than 12 clients → the list view is the
 * default and the board warns (P3 — show the exception; the board is for a
 * handful)." Twelve is `Board`'s own clamp (`components/portal/Board.tsx`
 * `MAX_COLS`) — past it the grid keeps twelve tracks and a thirteenth column starts a
 * second row, so a board with more than this many clients is no longer one row to
 * scan. The primitive does not export the number; the report asks for it.
 */
export const BOARD_MAX_COLUMNS = 12

/**
 * What the column with no client is called.
 *
 * The **em dash** is `ActionField`'s — the word the client picker offers on every
 * create form (`components/studio/ActionField.tsx` `ClientField`) — and this is the
 * page's one copy of it, read by the list cell, the Client facet, the board's own
 * column head and quick create's summary line. The read model spells it with a
 * hyphen (`readmodels/tasks-board.ts`); that string is on the wire and is not this
 * page's to change, so `columnsOf` reads the column's `client === null` and prints
 * this rather than passing the wire's word through.
 */
export const STUDIO_COLUMN = 'Internal — no client'

/* ------------------------------------------------------------------ the reads */

/**
 * The filters `tasks_list` and `tasks_summary` both take: the API's
 * `TASK_FILTER_FIELDS` and the client. One shape, produced once by
 * `facetsToTaskFilters`, so the list and the figures above it cannot be asked two
 * different questions.
 */
export type TaskFilterInput = Omit<StudioCallInput<'tasks_summary'>, 'max_columns'>

/** How many tasks one page brings: the list's and each board column's (Roberto, 18 Sep). */
export const TASKS_PAGE_SIZE = 50

export type BoardColumn = {
  /** The slug, or `''` for the studio's own column. Unique, so it is the React key. */
  readonly key: string
  readonly client: string | null
  readonly name: string
  /**
   * The head's number: the column's `matching` in the summary asked with the page's
   * filters, which is every task under it, loaded or not. With no facet set the page
   * still asks for the live tasks (`status` "Any" is `live`), and there `matching` is
   * the read model's `open`, the mockup's `6`, `7`, `3`.
   */
  readonly count: number
  readonly pastDue: boolean | null
}

export type TasksPageData = {
  /** The one input the list, each column and the figures are asked with. */
  readonly filters: TaskFilterInput
  /**
   * `tasks_summary` asked with `filters` and at most `BOARD_MAX_COLUMNS` columns.
   * Undefined until it lands. Every count on the page comes off it.
   */
  readonly summary: TasksSummary | undefined
  /**
   * `tasks_summary` asked with nothing: the nav badge's own read, so it costs no
   * request of its own. What the Client menu lists and which accounts the hero says
   * are past due on money, neither of which a filter should narrow.
   */
  readonly whole: TasksSummary | undefined
  /** Tasks in review under the page's filters, from the server. Undefined while it loads. */
  readonly reviewCount: number | undefined
  readonly columns: readonly BoardColumn[]
  /** A client's slug as the company is called, or the slug when no read names it. */
  readonly clientName: (slug: string) => string
  /** `service_code` → the catalog's own name for it. */
  readonly serviceName: (code: string | null) => string | undefined
  /** The catalog's services, for the Category menu. */
  readonly services: readonly { readonly code: string; readonly name: string }[]
  /** Everybody a task can be given to, for the Who menu (`tasks_list_assignees`). */
  readonly assignees: readonly { readonly id: string; readonly name: string }[]
  readonly isPending: boolean
  /**
   * Why the figures never came, or null: the server refused the question (a 4xx, a
   * client slug from an old link) or could not answer it at all (an outage).
   */
  readonly refused: ApiError | null
  /** Why the figures on screen could not be read again after a write, or null. */
  readonly refreshFailed: ApiError | null
  /** Asks for the figures again. */
  readonly retry: () => void
  /**
   * True while the figures on screen are the last question's and the new one's are
   * on the way: the columns too are the last question's, so nothing is drawn under them.
   */
  readonly isStale: boolean
}

/**
 * Everything `/studio/tasks` shows except the rows, which the list and each board
 * column page for themselves (`useTaskPages`).
 *
 * The summary is asked once, with the page's filters and `max_columns`, and serves
 * both views: the hero's figures, the line above the list and the board's columns.
 * The review figure is one more summary, the same filters with `status: review`;
 * with a Status already chosen it is that status's own answer and asks nothing.
 *
 * Nothing about who may see what is decided here any more. The restricted tier's
 * rule, no task carrying a `prepared_action`, is the server's (`tasks/actions.ts`
 * `filtersOf`), and because the page always sends at least `live` its list, its
 * figures and its columns all come from the same narrowed set.
 */
export function useTasksPage(facets: ListFacets, meId: string | null): TasksPageData {
  const filters = facetsToTaskFilters(facets, meId)
  /*
   * Inline: the question comes from the address, and the server can refuse it (a
   * client slug it has no client for, from an old link). The page says so and keeps
   * its filters, rather than handing the whole route to the error boundary.
   */
  const summary = useStudioRead(
    'tasks_summary',
    { ...filters, max_columns: BOARD_MAX_COLUMNS },
    // A refusal is not retried: in a background tab the retry would never come.
    // Live: the figures are other people's work as much as the reader's (`LIVE_REFETCH_MS`).
    { keepPrevious: true, inline: true, refusalIsFinal: true, live: true },
  )
  // Not `live` here: the sidebar's badge reads this same key and is (`useNavBadges`),
  // so it is asked again once a minute for all of its readers rather than once each.
  const whole = useStudioRead('tasks_summary', {}, { inline: true })
  const inReview = useStudioRead(
    'tasks_summary',
    { ...filters, status: 'review' },
    // Inline for the summary's reason above: the same question, refused the same way.
    {
      enabled: facets.status === 'all',
      keepPrevious: true,
      inline: true,
      refusalIsFinal: true,
      live: true,
    },
  )
  const services = useStudioRead('catalog_list_services', {}, { inline: true })
  const assignees = useStudioRead('tasks_list_assignees', {}, { inline: true })

  const serviceNames = new Map<string, string>()
  for (const service of services.data?.services ?? []) {
    serviceNames.set(service.code, service.name)
  }
  const clientNames = new Map<string, string>()
  for (const column of [...(whole.data?.clients ?? []), ...(summary.data?.clients ?? [])]) {
    if (column.client !== null) clientNames.set(column.client, column.name)
  }

  const reviewCount =
    facets.status === 'all'
      ? inReview.data?.matching
      : facets.status === 'review'
        ? summary.data?.matching
        : 0

  return {
    filters,
    summary: summary.data,
    whole: whole.data,
    reviewCount,
    columns: columnsOf(summary.data),
    clientName: (slug) => clientNames.get(slug) ?? slug,
    serviceName: (code) => (code === null ? undefined : serviceNames.get(code)),
    services: services.data?.services ?? [],
    assignees: assignees.data?.assignees ?? [],
    isPending: summary.isPending,
    // A failure with no figures at all is the question's; with figures, a refresh's.
    refused: summary.isError && summary.data === undefined ? summary.error : null,
    refreshFailed: summary.isError && summary.data !== undefined ? summary.error : null,
    retry: () => {
      void summary.refetch()
    },
    isStale: summary.isPlaceholderData,
  }
}

/** One paged read of tasks, the way the list and a board column draw it. */
export type TaskPages = {
  /** Every row loaded so far, in the server's order. */
  readonly rows: readonly TaskView[]
  readonly isPending: boolean
  /** True while a changed question's first page loads and the last answer is still up. */
  readonly isStale: boolean
  /** True when the server said there is a next page. */
  readonly hasMore: boolean
  readonly loadingMore: boolean
  /**
   * Why the last page did not come, or null: a Load more, or the first page itself.
   * The rows already loaded stay.
   */
  readonly failed: ApiError | null
  /**
   * True when what failed was reading the loaded pages again (the refresh after a
   * write), not a next page: the rows on screen are the ones from before the write.
   */
  readonly refreshFailed: boolean
  /**
   * The next page; after a failed Load more, the same page again; after a refused
   * cursor (a 400), a failed first page or a failed refresh, the whole walk again from
   * its first page.
   */
  readonly loadMore: () => void
}

/**
 * `tasks_list` for one question, a page of `TASKS_PAGE_SIZE` at a time.
 *
 * A task a write moved while somebody paged can come back on a later page too; its
 * first copy stands, so no row is drawn twice and no React key repeats.
 *
 * **A refused cursor is not retried.** A 400 on a later page means the server will
 * not read that cursor again, whatever is sent with it (one issued before a deploy
 * changed their shape, or under filters a change of tier has since changed), so
 * sending it again would fail for ever. The walk starts over instead: the pages
 * loaded are read again from the first, and each hands on a cursor issued now.
 */
export function useTaskPages(input: StudioCallInput<'tasks_list'>, enabled = true): TaskPages {
  /*
   * Live: the rows are read again every minute and when the tab comes back, because a
   * colleague, an agent and the lease sweep all move tasks while this page is open —
   * and a list that only re-read after the reader's own write showed their changes as
   * if that write had made them. A refresh that fails keeps the rows and says so
   * (`refreshFailed`), exactly as the refresh after a write always has.
   */
  const pages = useStudioPages('tasks_list', input, { enabled, inline: true, live: true })
  const queryClient = useQueryClient()
  /*
   * The question the list is asking, as one value — and, in a ref, the one it is
   * asking **now**, for a press that outlives the render it was made in. A Load more
   * is two reads, and the order or a filter can be changed between them: `pages` then
   * speaks for the new question, and the second read brought a second page of a list
   * nobody had asked for more of.
   */
  const asked = hashKey(studioPagesKey('tasks_list', input))
  const asking = useRef(asked)
  asking.current = asked
  const stillAsked = (): boolean => asking.current === asked
  /** The question a Load more is reading the loaded pages again for, on its way to the next. */
  const [walking, setWalking] = useState<string | null>(null)
  const failed = pages.isError ? pages.error : null
  const refused = failed !== null && isApiProblem(failed) && failed.status === 400
  const seen = new Set<string>()
  const rows: TaskView[] = []
  for (const page of pages.data?.pages ?? []) {
    for (const task of page.tasks) {
      if (seen.has(task.ref)) continue
      seen.add(task.ref)
      rows.push(task)
    }
  }
  return {
    rows,
    isPending: pages.isPending,
    isStale: pages.isPlaceholderData,
    hasMore: pages.hasNextPage,
    loadingMore: pages.isFetchingNextPage || walking === asked,
    failed,
    refreshFailed: pages.isRefetchError,
    loadMore: () => {
      /*
       * What the list's own read is doing **now**, asked of the cache and not of
       * `pages`: a click that ends a hold starts a re-read in the same turn, before
       * this component has been drawn again to hear of it.
       */
      const reading = () =>
        queryClient
          .getQueryCache()
          .find({ queryKey: studioPagesKey('tasks_list', input), exact: true })?.state
      /** The loaded pages being read again — as opposed to a next page being added. */
      const rereading = (): boolean => {
        const now = reading()
        return now?.fetchStatus === 'fetching' && now.fetchMeta?.fetchMore === undefined
      }
      /*
       * The next page — **and again, where a save's re-read took its place.** A save
       * answered while the next page is being read reads the loaded pages again
       * *instead*: the cache gives up a next page for a re-read, and reads only what
       * was loaded. The press was dropped with nothing said — the list as it was, and
       * the button to be pressed a second time. A few times and no more: it is one
       * press, and a list being saved to that often has other things to say.
       */
      const addNext = async (): Promise<void> => {
        for (let tries = 0; tries < NEXT_PAGE_TRIES; tries += 1) {
          const had = pagesLoaded(reading()?.data)
          const next = await pages.fetchNextPage()
          // Added, refused, or nothing after it: the press has been answered.
          if (pagesLoaded(reading()?.data) > had || next.isError || !next.hasNextPage) return
          // Not added: a re-read took its place. Behind that one, and whatever replaced it.
          while (stillAsked() && rereading()) await pages.refetch({ cancelRefetch: false })
          if (!stillAsked() || reading()?.status === 'error') return
        }
      }
      /*
       * Nothing loaded yet, a cursor the server will not read, or a refresh that
       * failed: the walk is read again from its first page and that is all.
       */
      if (refused || rows.length === 0 || pages.isRefetchError) {
        void pages.refetch()
        return
      }
      // A Load more that failed asks for that page again; its rows were read just now.
      if (failed !== null) {
        /*
         * **Behind a re-read that is already on its way, not over it.** Asking for a
         * next page cancels a refetch in flight, and the one in flight may be the one
         * a hold owed (`api/hooks.ts` `useCatchUp`) — the click that leaves a date
         * cell can be this very button. Cancelled, the pages already read stayed as
         * they were for another minute, offering what had just been refused.
         *
         * Asked of the cache, at the click, and not of `pages` (`reading`, above).
         */
        if (!rereading()) {
          void addNext()
          return
        }
        void (async () => {
          /*
           * Behind **every** re-read on its way, one after another. The one joined can
           * itself be replaced — the date cell this click left has a save of its own,
           * and that save's re-read cancels the catch-up's — and a replaced read ends
           * with no answer, which read as "it failed" and dropped the press.
           *
           * **Of the list this press was made on, and for no longer than it is the one
           * asked** (`stillAsked`): with the order changed under it, `pages` reads the
           * new list, and joining "its" re-read read that one again each time round.
           */
          while (stillAsked() && rereading()) await pages.refetch({ cancelRefetch: false })
          // A re-read that failed is said (`refreshFailed`), not gone over: the next
          // page arriving would clear the error with the old pages still up.
          if (stillAsked() && reading()?.status !== 'error') await addNext()
        })()
        return
      }
      /*
       * **The next page is read as the end of one walk from the first**, not added to
       * pages read earlier.
       *
       * A cursor says "after the last row I have", and the rows before it are as old
       * as the last refresh — up to a minute. A task somebody moved up the order in
       * that minute is before the cursor now and was after it then: on no page that
       * is asked for, so missing from the list with the count above it saying
       * otherwise; and one moved down is on both. Reading the loaded pages again
       * first, each from the cursor the one before it hands on *now*, makes the whole
       * list the product of a single reading, which is as consistent as a list other
       * people are writing to can be. It costs the pages already loaded, once per
       * Load more, on a list that is fifty rows a page.
       */
      setWalking(asked)
      void (async () => {
        let fresh = await pages.refetch()
        /*
         * **And behind every re-read that took this one's place.** A save answered
         * during the walk reads the list again *over* it, and a second save over
         * that — and the walk's own wait ends there, with no answer and the pages as
         * they were. Asking for the next page then read **over** the save's re-read
         * and added a page to rows from before the save: the cell that had just been
         * changed showed its old value, with the save landed and nothing left to
         * correct it for a minute.
         */
        while (stillAsked() && rereading()) fresh = await pages.refetch({ cancelRefetch: false })
        // Of this question, and no other: `fresh` is the list as it is asked now.
        if (!stillAsked()) return
        // The walk just made says whether there is still a page after it.
        const more = fresh.data?.pages.at(-1)?.next_cursor ?? null
        if (fresh.isError || more === null) return
        await addNext()
      })().finally(() => {
        setWalking((now) => (now === asked ? null : now))
      })
    },
  }
}

/** How many times one Load more asks for its page when a save's re-read takes its place. */
const NEXT_PAGE_TRIES = 3

/** How many pages a paged read holds, read off the cache's own copy of it. */
function pagesLoaded(data: unknown): number {
  const pages = (data as { readonly pages?: readonly unknown[] } | undefined)?.pages
  return pages?.length ?? 0
}

/** The list's own question: the page's filters, its order, fifty at a time. */
export function listInput(
  filters: TaskFilterInput,
  sort: ListSort,
  dir: ListDir,
): StudioCallInput<'tasks_list'> {
  return { ...filters, sort, dir, limit: TASKS_PAGE_SIZE }
}

/**
 * One board column's question: the page's filters narrowed to the column's client,
 * or to the studio's own work, by due date as the board has always been.
 *
 * The column's own client replaces the page's client condition rather than joining
 * it: the API refuses `client` with `no_client`, and a column is only ever drawn for
 * a client the page's filters already allow.
 */
export function columnInput(
  filters: TaskFilterInput,
  column: Pick<BoardColumn, 'client'>,
): StudioCallInput<'tasks_list'> {
  const { client: _client, no_client: _noClient, ...rest } = filters
  return {
    ...rest,
    ...(column.client === null ? { no_client: true } : { client: column.client }),
    sort: 'due',
    dir: 'asc',
    limit: TASKS_PAGE_SIZE,
  }
}

/** True when an agent prepared a money call that only a manager can run. */
export function hasPreparedAction(task: TaskView): boolean {
  return preparedActionOf(task) !== null
}

/* --------------------------------------------------------------- the filters */

/** Columns the list can order by — Client, Who, Due. */
export type ListSort = 'client' | 'who' | 'due'
export type ListDir = 'asc' | 'desc'

/** Next click on a sortable head: same column flips; a new column starts ascending. */
export function nextSort(
  current: ListSort,
  currentDir: ListDir,
  clicked: ListSort,
): { readonly sort: ListSort; readonly dir: ListDir } {
  if (clicked === current) {
    return { sort: current, dir: currentDir === 'asc' ? 'desc' : 'asc' }
  }
  return { sort: clicked, dir: 'asc' }
}

/* ---------------------------------------------------------- list facet filters */

/** Due window for the Due date select. */
export type ListWhen = 'all' | 'overdue' | 'week' | 'undated'

/** Blocked-on-client select — same rule as the hero's blocked count. */
export type ListBlocked = 'all' | 'yes' | 'no'

/**
 * Status facet — live board states, plus `done` for the Completed working view.
 * `claimed` is offered (#51): a task somebody has taken and not started is a state a
 * reader filters for. `cancelled` never appears. `done` is off the
 * board by default and only surfaces when Status is Completed.
 */
export type ListStatus = 'all' | 'open' | 'claimed' | 'in_progress' | 'blocked' | 'review' | 'done'

/**
 * Priority facet — `all`, or one of `TASK_PRIORITY_MIN`…`TASK_PRIORITY_MAX`.
 *
 * A **number**, not the digit as a string, and that is about the address bar rather
 * than about this file. The router's default `stringifySearch` quotes any string
 * that would parse back as JSON, so `'1'` left the page as `?priority=%221%22` — a
 * link nobody can type and nobody can read. A number round-trips as `?priority=1`.
 * `TasksSearchSchema` still accepts the quoted form, so the links already sent stay
 * good.
 */
export type ListPriority = 'all' | 1 | 2 | 3 | 4 | 5

export type ListFacets = {
  readonly client: string
  readonly who: string
  readonly when: ListWhen
  readonly blocked: ListBlocked
  /** `all`, `s:<service_code>`, or `q:<queue>` — card under-line without money refs. */
  readonly category: string
  /** Case-insensitive text over title / instructions / ref / blocked_reason. */
  readonly q: string
  readonly priority: ListPriority
  readonly status: ListStatus
  /** Explicit queue facet — Category may still name a service or a queue key. */
  readonly queue: string
}

/** Defaults every facet returns to when Clear all / a working view resets the URL. */
export const LIST_FACET_DEFAULTS: ListFacets = {
  client: 'all',
  who: 'all',
  when: 'all',
  blocked: 'all',
  category: 'all',
  q: '',
  priority: 'all',
  status: 'all',
  queue: 'all',
}

/**
 * Prefill `tasks_create` from the board filters the reader is looking at (#42).
 * "All clients" / Internal leave client unset; a real slug becomes the default.
 * Queue comes from the queue facet, or from a `q:<queue>` category chip.
 * Who = Me / Unassigned become the assignee picker (#41).
 */
export function createDefaultsFromFacets(
  facets: Pick<ListFacets, 'client' | 'queue' | 'priority' | 'category' | 'who'>,
  meId: string | null = null,
): Readonly<Record<string, unknown>> {
  const defaults: Record<string, unknown> = {}
  if (facets.client !== 'all' && facets.client !== 'studio') {
    defaults['client'] = facets.client
  }
  if (facets.queue !== 'all') {
    defaults['queue'] = facets.queue
  } else if (facets.category.startsWith('q:')) {
    const queue = facets.category.slice(2)
    if ((TASK_QUEUES as readonly string[]).includes(queue)) {
      defaults['queue'] = queue
    }
  }
  if (facets.priority !== 'all') {
    defaults['priority'] = facets.priority
  }
  if (facets.who === 'none') {
    defaults['assignee'] = 'none'
  } else if (meId !== null && facets.who === meId) {
    defaults['assignee'] = `user:${meId}`
  }
  return defaults
}

/**
 * The longest search `tasks_list` takes (`TASK_FILTER_FIELDS` `q`, 200). A longer one
 * is cut here rather than refused there: a refused read takes the page down.
 */
const SEARCH_MAX = 200

/**
 * An input no task can match, for a facet the API cannot be asked about.
 *
 * `service_code` and `no_service_code` together ask for a task on this service and on
 * no service at once; the API reads the two as `and` (session 40), so nothing matches
 * and the list, the figures and the columns all say so. It is what the page answered
 * before this session for the same facets, when it narrowed the rows itself: a
 * `q:<queue>` Category beside a different Queue, or a Queue, a Who or a Client in the
 * address that is not one at all, matched no task. Sending them instead would be a
 * refusal, and a refused read takes the page down.
 */
const MATCHES_NOTHING = { service_code: 'none', no_service_code: true } as const

const PersonIdSchema = z.uuid()

function isQueue(value: string): value is TaskQueue {
  return (TASK_QUEUES as readonly string[]).includes(value)
}

/**
 * The URL's facets as the input `tasks_list` and `tasks_summary` both take: the one
 * mapping from what the page shows to what the server is asked (session 42).
 *
 * - **Status "Any" is `live`**, the tasks still in hand, as the page has always meant
 *   it; Completed is `status: done`; any other status is itself.
 * - Client `studio` is `no_client`; Who `none` is `assignee_kind: none`, `me` is the
 *   signed-in person, anything else a person's or an agent's id.
 * - Due `overdue` is `overdue`; `week` and `undated` are `due_window`, so "this week"
 *   is the server's `taskWeekWindow`: from the start of today, in the studio's zone.
 * - Category `s:<code>` is a service; `q:<queue>`, which old links still carry, is a
 *   queue's own work, that queue and no service.
 * - Search is trimmed, left out when empty, and cut at `SEARCH_MAX`.
 *
 * Sort and direction are not here: the summary takes neither.
 */
export function facetsToTaskFilters(facets: ListFacets, meId: string | null): TaskFilterInput {
  const input: TaskFilterInput = {}
  let impossible = false

  if (facets.client === 'studio') input.no_client = true
  else if (facets.client !== 'all') {
    if (ClientSlugSchema.safeParse(facets.client).success) input.client = facets.client
    else impossible = true
  }

  const who = facets.who === 'me' ? meId : facets.who
  if (who === 'none') input.assignee_kind = 'none'
  else if (who !== null && who !== 'all') {
    if (PersonIdSchema.safeParse(who).success) input.assignee_id = who
    else impossible = true
  }

  if (facets.when === 'overdue') input.overdue = true
  else if (facets.when === 'week') input.due_window = 'week'
  else if (facets.when === 'undated') input.due_window = 'undated'

  if (facets.category.startsWith('s:') && facets.category.length > 2) {
    input.service_code = facets.category.slice(2)
  } else if (facets.category.startsWith('q:')) {
    const queue = facets.category.slice(2)
    if (isQueue(queue)) {
      input.queue = queue
      input.no_service_code = true
    } else impossible = true
  }

  if (facets.queue !== 'all') {
    if (!isQueue(facets.queue) || (input.queue !== undefined && input.queue !== facets.queue)) {
      impossible = true
    } else input.queue = facets.queue
  }

  if (facets.priority !== 'all') input.priority = facets.priority

  if (facets.status === 'all') input.live = true
  else input.status = facets.status

  if (facets.blocked !== 'all') input.blocked_on_client = facets.blocked === 'yes'

  const q = facets.q.trim().slice(0, SEARCH_MAX).trim()
  if (q !== '') input.q = q

  return impossible ? { ...input, ...MATCHES_NOTHING } : input
}

/* ----------------------------------------------------------- working views */

/**
 * One-click triage presets. Each patch is applied on top of `LIST_FACET_DEFAULTS`
 * so a second press does not stack with leftover Client / Who filters.
 */
export type WorkingViewId =
  'mine' | 'unassigned' | 'overdue' | 'week' | 'waiting' | 'review' | 'nodate' | 'completed'

export type WorkingView = {
  readonly id: WorkingViewId
  readonly label: string
  /** Facet patch; `who: 'me'` is resolved to the signed-in user id at apply time. */
  readonly patch: Partial<ListFacets>
}

export const WORKING_VIEWS: readonly WorkingView[] = [
  { id: 'mine', label: 'My work', patch: { who: 'me' } },
  { id: 'unassigned', label: 'Unassigned', patch: { who: 'none' } },
  { id: 'overdue', label: 'Overdue', patch: { when: 'overdue' } },
  { id: 'week', label: 'Due this week', patch: { when: 'week' } },
  { id: 'waiting', label: 'Waiting on client', patch: { blocked: 'yes' } },
  { id: 'review', label: 'Needs review', patch: { status: 'review' } },
  { id: 'nodate', label: 'No date', patch: { when: 'undated' } },
  { id: 'completed', label: 'Completed', patch: { status: 'done' } },
]

/** Facets a working view would write, with `who: 'me'` resolved. */
export function facetsForWorkingView(view: WorkingView, meId: string | null): ListFacets {
  const who =
    view.patch.who === 'me'
      ? (meId ?? LIST_FACET_DEFAULTS.who)
      : (view.patch.who ?? LIST_FACET_DEFAULTS.who)
  return { ...LIST_FACET_DEFAULTS, ...view.patch, who }
}

/** Which working view matches the current facets exactly, or null. */
export function activeWorkingView(facets: ListFacets, meId: string | null): WorkingViewId | null {
  for (const view of WORKING_VIEWS) {
    const expected = facetsForWorkingView(view, meId)
    if (
      facets.client === expected.client &&
      facets.who === expected.who &&
      facets.category === expected.category &&
      facets.when === expected.when &&
      facets.blocked === expected.blocked &&
      facets.q === expected.q &&
      facets.priority === expected.priority &&
      facets.status === expected.status &&
      facets.queue === expected.queue
    ) {
      return view.id
    }
  }
  return null
}

/* -------------------------------------------------------- active filter chips */

export type ActiveFilterChip = {
  /** Facet key to clear back to its default. */
  readonly key: keyof ListFacets
  readonly label: string
}

/**
 * Removable chips for every non-default facet. Labels come from the page so Client /
 * Who / Category can print the human name rather than the slug.
 */
export function activeFilterChips(
  facets: ListFacets,
  labels: {
    readonly client: (value: string) => string
    readonly who: (value: string) => string
    readonly category: (value: string) => string
    readonly queue: (value: string) => string
  },
): readonly ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = []
  if (facets.q !== LIST_FACET_DEFAULTS.q) {
    chips.push({ key: 'q', label: `Search: ${facets.q}` })
  }
  if (facets.client !== LIST_FACET_DEFAULTS.client) {
    chips.push({ key: 'client', label: `Client: ${labels.client(facets.client)}` })
  }
  if (facets.who !== LIST_FACET_DEFAULTS.who) {
    chips.push({ key: 'who', label: `Who: ${labels.who(facets.who)}` })
  }
  if (facets.category !== LIST_FACET_DEFAULTS.category) {
    chips.push({ key: 'category', label: `Category: ${labels.category(facets.category)}` })
  }
  if (facets.queue !== LIST_FACET_DEFAULTS.queue) {
    chips.push({ key: 'queue', label: `Queue: ${labels.queue(facets.queue)}` })
  }
  if (facets.priority !== LIST_FACET_DEFAULTS.priority) {
    chips.push({ key: 'priority', label: `Priority: ${priorityWord(Number(facets.priority))}` })
  }
  if (facets.status !== LIST_FACET_DEFAULTS.status) {
    const status = facets.status
    chips.push({
      key: 'status',
      label: `Status: ${status === 'all' ? status : STATUS_WORD_CHIP[status]}`,
    })
  }
  if (facets.when !== LIST_FACET_DEFAULTS.when) {
    const when = facets.when
    chips.push({
      key: 'when',
      label: `Due: ${when === 'all' ? when : WHEN_CHIP[when]}`,
    })
  }
  if (facets.blocked !== LIST_FACET_DEFAULTS.blocked) {
    chips.push({
      key: 'blocked',
      label: facets.blocked === 'yes' ? 'Blocked on client' : 'Not blocked on client',
    })
  }
  return chips
}

const WHEN_CHIP: Record<Exclude<ListWhen, 'all'>, string> = {
  overdue: 'Overdue',
  week: 'This week',
  undated: 'No date',
}

const STATUS_WORD_CHIP: Record<Exclude<ListStatus, 'all'>, string> = {
  open: 'Open',
  claimed: 'Claimed',
  in_progress: 'In progress',
  blocked: 'Blocked',
  review: 'Needs review',
  done: 'Completed',
}

/* --------------------------------------------------------------- the columns */

/**
 * One column per client the summary names, in its order, the studio's own last.
 *
 * The summary is the only source: each column then pages its own tasks
 * (`columnInput`), so a column exists because the server counted tasks under it, and
 * never because a row happened to be loaded. Clients past `max_columns` are the
 * summary's `others`, which the board states as a count.
 */
export function columnsOf(summary: TasksSummary | undefined): readonly BoardColumn[] {
  return (summary?.clients ?? []).map((column) => ({
    key: column.client ?? '',
    client: column.client,
    /* One spelling of the studio's own column on this page — see `STUDIO_COLUMN`. */
    name: column.client === null ? STUDIO_COLUMN : column.name,
    count: column.matching,
    pastDue: column.past_due,
  }))
}

/**
 * The studio column only: one group per queue that still has a card, in
 * `TASK_QUEUES` order, labelled with the same words the detail page uses.
 *
 * Empty queues are omitted — a placeholder for work that is not there would
 * read as a filter the page does not have.
 */
export type QueueGroup = {
  readonly queue: TaskQueue
  readonly label: string
  readonly tasks: readonly TaskView[]
}

export function groupByQueue(tasks: readonly TaskView[]): readonly QueueGroup[] {
  const groups: QueueGroup[] = []
  for (const queue of TASK_QUEUES) {
    const mine = tasks.filter((task) => task.queue === queue)
    if (mine.length === 0) continue
    groups.push({ queue, label: QUEUE_WORD[queue], tasks: mine })
  }
  return groups
}

/* ------------------------------------------------------------------ the view */

/**
 * Which of the two views to draw.
 *
 * Default is the **list** — triage is one table by due date — unless the reader
 * last chose Board and that preference is still in `localStorage` (#24). `?view=`
 * in the URL always wins, so a shared link stays honest. Past `BOARD_MAX_COLUMNS`
 * the board still warns when asked for.
 */
export function viewFor(chosen: BoardView | undefined, _columns: number): BoardView {
  if (chosen !== undefined) return chosen
  return preferredView() ?? 'list'
}

const VIEW_PREF_KEY = 'fes.studio.tasks.view'

/** Last Board/List choice, or null when unset / unreadable. */
export function preferredView(): BoardView | null {
  try {
    const raw = localStorage.getItem(VIEW_PREF_KEY)
    return raw === 'board' || raw === 'list' ? raw : null
  } catch {
    return null
  }
}

/** Remember the Board/List toggle for the next visit without `?view=`. */
export function rememberView(view: BoardView): void {
  try {
    localStorage.setItem(VIEW_PREF_KEY, view)
  } catch {
    /* private mode / quota — preference is optional */
  }
}

const SCROLL_PREF_KEY = 'fes.studio.tasks.scrollY'

/** Save how far the Tasks page was scrolled (#23) — used when leaving for a detail. */
export function rememberScrollY(y: number): void {
  try {
    sessionStorage.setItem(
      SCROLL_PREF_KEY,
      String(Math.max(0, Number.parseInt(String(y), 10) || 0)),
    )
  } catch {
    /* private mode / quota — restoration is optional */
  }
}

/**
 * Consume the saved scroll once. Returns null when there is nothing useful to
 * restore (missing, zero, or unreadable).
 */
export function takeScrollY(): number | null {
  try {
    const raw = sessionStorage.getItem(SCROLL_PREF_KEY)
    if (raw === null) return null
    sessionStorage.removeItem(SCROLL_PREF_KEY)
    const y = Number(raw)
    return Number.isFinite(y) && y > 0 ? y : null
  } catch {
    return null
  }
}
